import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField,
  AritoIcon
} from '@/components/custom/erp';
import {
  hanThanhToanSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { HanThanhToan, KhachHang, NhanVien } from '@/types/schemas';
import { useDonHangMuaTrongNuocSearchContext } from '../../context';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}
export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const isViewMode = formMode === 'view';
  const { setValue } = useFormContext();
  const ngay_ct = useWatch({ name: 'ngay_ct' });
  const { setNgayCt } = useDonHangMuaTrongNuocSearchContext();

  useEffect(() => {
    if (ngay_ct) {
      setValue('ngay_lct', ngay_ct);
    }
    setNgayCt(ngay_ct);
  }, [ngay_ct, setValue, setNgayCt]);

  const rowClassName = 'flex items-center gap-2 xl:gap-3';
  const leftLabelClassName = 'w-[180px] shrink-0 text-[13px] xl:w-[195px]';
  const middleLabelClassName = 'w-[120px] shrink-0 text-[13px] xl:w-[132px]';
  const rightLabelClassName = 'w-[104px] shrink-0 text-[13px] xl:w-[112px] 2xl:w-[127px]';

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[1460px] p-3 xl:min-w-0 xl:p-4'>
        <div className='grid grid-cols-12 gap-4 xl:gap-6'>
          <div className='col-span-6 min-w-0 space-y-1'>
            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã nhà cung cấp</Label>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <div className='w-[180px] xl:w-[200px]'>
                  <SearchField<KhachHang>
                    disabled={isViewMode}
                    name='ma_kh'
                    searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
                    searchColumns={khachHangSearchColumns}
                    value={state.khachHang?.customer_code || state.khachHang?.ma_kh}
                    onRowSelection={actions.setKhachHang}
                    columnDisplay='customer_code'
                    dialogTitle='Danh mục nhà cung cấp'
                    className='w-full'
                  />
                </div>

                <div className='flex min-w-0 flex-1 items-center gap-2'>
                  <Label className='w-20 shrink-0 text-[13px]'>Mã số thuế</Label>
                  <div className='min-w-0 max-w-[220px] flex-1'>
                    <FormFieldV2
                      name='ma_so_thue'
                      type='text'
                      disabled={isViewMode}
                      placeholder='Nhập và tra cứu'
                      value={state.khachHang?.tax_code || state.khachHang?.mst}
                    />
                  </div>
                  <button
                    type='button'
                    className='flex items-center justify-center border border-gray-200 px-3 py-1.5'
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('Refresh');
                    }}
                  >
                    <AritoIcon icon={15} className='shrink-0' />
                  </button>
                  <button
                    type='button'
                    className='flex items-center justify-center border border-gray-200 px-3 py-1.5'
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('New');
                    }}
                  >
                    <AritoIcon icon={888} />
                  </button>
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Tên nhà cung cấp</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='ten_kh_thue'
                  type='text'
                  disabled={isViewMode}
                  placeholder='Tên nhà cung cấp/đơn vị'
                  value={state.khachHang?.customer_name || state.khachHang?.ten_kh}
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Địa chỉ</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='dia_chi'
                  type='text'
                  disabled={isViewMode}
                  placeholder='Địa chỉ nhà cung cấp'
                  value={state.khachHang?.address}
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Mã nhân viên</Label>
              <SearchField<NhanVien>
                type='text'
                name='ma_nvmh'
                searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
                columnDisplay='ma_nhan_vien'
                displayRelatedField='ho_ten_nhan_vien'
                searchColumns={nhanVienSearchColumns}
                dialogTitle='Danh sách nhân viên mua hàng'
                value={state.nhanVien?.ma_nv || state.khachHang?.sales_rep_data?.ma_nhan_vien}
                relatedFieldValue={state.nhanVien?.ten_nv || state.khachHang?.sales_rep_data?.ho_ten_nhan_vien}
                onRowSelection={actions.setNhanVien}
                disabled={isViewMode}
                className='w-100'
              />
            </div>

            <div className={rowClassName}>
              <Label className={leftLabelClassName}>Diễn giải</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2 name='dien_giai' type='text' disabled={isViewMode} />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0 space-y-1'>
            <div className={rowClassName}>
              <FormFieldV2
                name='ong_ba'
                label='Người liên hệ'
                type='text'
                disabled={isViewMode}
                placeholder='Nhập tên người liên hệ'
                value={state.khachHang?.contact_person}
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                className='w-[220px]'
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='e_mail'
                label='Email'
                type='text'
                disabled={isViewMode}
                placeholder='Email'
                value={state.khachHang?.email}
                labelClassName={middleLabelClassName}
                fieldClassName='w-full'
                className='w-[220px]'
              />
            </div>

            <div className={rowClassName}>
              <Label className={middleLabelClassName}>Hạn thanh toán</Label>
              <div className='w-[220px]'>
                <SearchField<HanThanhToan>
                  type='text'
                  name='ma_tt'
                  value={state.hanThanhToan?.ten_ht || state.khachHang?.payment_term_data?.ma_tt || ''}
                  searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                  columnDisplay='ma_tt'
                  searchColumns={hanThanhToanSearchColumns}
                  dialogTitle='Danh mục hạn thanh toán'
                  onRowSelection={actions.setHanThanhToan}
                  placeholder='Nhập hạn thanh toán'
                  disabled={isViewMode}
                  className='w-full'
                />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0 space-y-2'>
            <DocumentNumberFieldV2
              ma_ct={MA_CHUNG_TU.MUA_HANG.DON_HANG_MUA_TRONG_NUOC}
              quyenChungTu={state.quyenChungTu}
              onQuyenChungTuChange={actions.setQuyenChungTu}
              soChungTu={state.soChungTu}
              onSoChungTuChange={actions.setSoChungTu}
              disabled={isViewMode}
              className='flex items-center gap-3'
              classNameSearchField='min-w-0 flex-1'
              labelClassName={rightLabelClassName}
              formMode={formMode}
            />

            <FormFieldV2
              label='Ngày chứng từ'
              name='ngay_ct'
              type='date'
              labelClassName={rightLabelClassName}
              className='w-full'
              disabled={isViewMode}
            />

            <FormFieldV2
              label='Ngày hiệu lực'
              name='ngay_hl'
              type='date'
              labelClassName={rightLabelClassName}
              className='w-full'
              disabled={isViewMode}
            />

            <FormFieldV2
              label='Số hợp đồng'
              name='so_hd'
              type='text'
              labelClassName={rightLabelClassName}
              className='w-full'
              disabled={isViewMode}
            />

            <CurrencyInputV2
              formMode={formMode}
              className='flex items-center gap-3'
              classNameInput='min-w-0 flex-1'
              labelClassName={rightLabelClassName}
            />

            <FormFieldV2
              name='status'
              type='select'
              label='Trạng thái'
              disabled={isViewMode}
              labelClassName={rightLabelClassName}
              fieldClassName='w-full'
              className='w-full'
              options={[
                { value: '0', label: 'Lập chứng từ' },
                { value: '3', label: 'Chờ duyệt' },
                { value: '4', label: 'Đang duyệt' },
                { value: '5', label: 'Đã duyệt' }
              ]}
            />

            <div className='mt-1 flex'>
              <div className='h-2 w-[104px] shrink-0 xl:w-[112px] 2xl:w-[127px]' />
              <FormFieldV2
                name='transfer_yn'
                type='checkbox'
                disabled
                label='Chuyển dữ liệu'
                labelClassName='w-32 text-[13px]'
                value={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
